import { User } from "@fairfleet/geotab";
import { useMemo } from "react";
import { useCurrentTripByDeviceIds } from "@/hooks/trips/useCurrentTripByDeviceIds";
import { useUsersByIds } from "@/hooks/users/useUsersByIds";

type CurrentTrip = {
  driver?: { id?: string };
};

/** Which driver is currently on which vehicle, and who those drivers are. */
export function useDriverAssignments(deviceIds: string[]) {
  const { data: currentTrips = {}, isLoading: isLoadingTrips } = useCurrentTripByDeviceIds(deviceIds);

  const driverIdByDeviceId = useMemo(() => {
    const result: Record<string, string | undefined> = {};

    for (const [deviceId, trip] of Object.entries(currentTrips as Record<string, CurrentTrip>)) {
      result[deviceId] = trip?.driver?.id;
    }

    return result;
  }, [currentTrips]);

  const deviceIdByDriverId = useMemo(() => {
    const result = new Map<string, string>();

    for (const [deviceId, driverId] of Object.entries(driverIdByDeviceId)) {
      if (driverId) result.set(driverId, deviceId);
    }

    return result;
  }, [driverIdByDeviceId]);

  const driverIds = useMemo(() => [...deviceIdByDriverId.keys()], [deviceIdByDriverId]);

  const { data: driverPages, isLoading: isLoadingDrivers } = useUsersByIds(driverIds);

  const driverById = useMemo(() => {
    const result = new Map<string, User>();

    for (const user of (driverPages ?? []).flat().filter(Boolean) as User[]) {
      if (user.id) result.set(user.id, user);
    }

    return result;
  }, [driverPages]);

  return {
    driverIdByDeviceId,
    deviceIdByDriverId,
    driverById,
    isLoading: isLoadingTrips || isLoadingDrivers,
  };
}
